import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import RefreshIcon from '@material-ui/icons/Refresh';
import { FormattedMessage, useIntl } from 'react-intl';
import * as actions from 'actions';
import api from 'api';
import HostModifyBookingSection from './host-modify-booking-section';
import CancelButton from './cancel-button';
import AcceptedTerms from './accepted-terms';

const IsHostBookingPending = ({
  booking,
  disableButtons,
  offerChanged,
  payinLocked,
  isHost,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [reloading, setReloading] = React.useState(false);
  // Direct API call (rather than fetchBookingRequest saga) keeps the booking
  // view rendered while reloading -- the saga reducer wipes singleBooking,
  // collapsing the alert+icon. 401 is handled by the global axios interceptor.
  const reloadBooking = async () => {
    if (reloading) return;
    setReloading(true);
    try {
      const data = await api.fetchBooking(booking.id);
      dispatch(actions.fetchBookingSuccess({ data }));
    } catch (e) {
      // 401 is handled by the global axios interceptor; surface other
      // failures via a snackbar so the host knows the reload didn't run.
      if (e?.status !== 401) {
        dispatch(
          actions.showSnackbar({
            severity: 'error',
            message: {
              id: 'bookingInfo.reloadFailed',
              defaultMessage: "Couldn't refresh the booking. Try again in a moment.",
            },
          }),
        );
      }
    } finally {
      setReloading(false);
    }
  };

  return (
    <Box>
      <Box pl={1.5} mb={3}>
        <AcceptedTerms booking={booking} />
      </Box>
      {payinLocked && (
        <Box mb={2}>
          <Alert
            severity="info"
            style={{ display: 'inline-flex' }}
            action={
              <IconButton
                size="small"
                color="inherit"
                aria-label={intl.formatMessage({
                  id: 'bookingInfo.reload',
                  defaultMessage: 'Reload booking',
                })}
                disabled={reloading}
                onClick={reloadBooking}
              >
                {reloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            }
          >
            <FormattedMessage
              id="bookingInfo.payinLockedHostCancel"
              defaultMessage="A guest is paying right now. You can't change or remove the offer during a payment in progress."
            />
          </Alert>
        </Box>
      )}
      {!payinLocked && offerChanged && (
        <Box mb={2}>
          <Alert severity="warning" style={{ display: 'inline-flex' }}>
            <FormattedMessage
              id="bookingInfo.offerStaleHostCancel"
              defaultMessage="The booking has changed since you opened this page. Try again if you still want to remove the offer."
            />
          </Alert>
        </Box>
      )}
      <HostModifyBookingSection booking={booking} disableButtons={disableButtons || payinLocked} />
      &nbsp;
      <CancelButton
        {...{
          isHost,
          onCancel,
          disableButtons: disableButtons || payinLocked,
          booking,
        }}
      />
    </Box>
  );
};

IsHostBookingPending.propTypes = {
  booking: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })
    .isRequired,
  disableButtons: PropTypes.bool,
  offerChanged: PropTypes.bool,
  payinLocked: PropTypes.bool,
  isHost: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default IsHostBookingPending;
